import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Box, 
  CircularProgress, 
  Alert,
  Stack,
  IconButton,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import { getAllJournalEntries } from '../../services/bennyJournalService';
import { getAllWikiEntries } from '../../services/wikiService';
import { linkifyWikiEntries } from '../../utils/wikiLinkUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// It's good practice to have a separate CSS file or use styled-components/JSS for more complex styling,
// but for this example, we'll use sx props and rely on existing classes from TownSquare.css where appropriate.
// We will also define some new styles directly or via sx props to emulate the fantasy theme.

const BennyJournalDisplay = () => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wikiEntries, setWikiEntries] = useState([]);
  const navigate = useNavigate();
  const { currentUser, firestoreUser } = useAuth();

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        setLoading(true);
        const [entries, wikiData] = await Promise.all([
          getAllJournalEntries(),
          getAllWikiEntries()
        ]);
        
        // Log the downloadURL for each entry to help diagnose
        entries.forEach(entry => {
          if (entry.downloadURL) {
            console.log(`BennyJournalDisplay - Entry ID: ${entry.id}, Title: "${entry.title}", downloadURL: ${entry.downloadURL}`);
            // Check if the URL seems to be a PDF URL
            const isPdfUrl = entry.downloadURL.toLowerCase().includes('.pdf?alt=media') || entry.downloadURL.toLowerCase().endsWith('.pdf');
            if (!isPdfUrl) {
              console.warn(`BennyJournalDisplay - WARNING: Entry ID ${entry.id} ("${entry.title}") has a downloadURL that might not be a PDF: ${entry.downloadURL}`);
            }
          } else {
            console.log(`BennyJournalDisplay - Entry ID: ${entry.id}, Title: "${entry.title}" has no downloadURL.`);
          }
        });

        setJournalEntries(entries);
        setWikiEntries(wikiData || []);
        setError('');
      } catch (err) {
        console.error("BennyJournalDisplay - Error fetching journal entries:", err);
        setError('Failed to load journal entries. ' + err.message);
        setJournalEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, []);

  if (loading) {
    return (
      <Container 
        className="town-square-container" // Using class from TownSquare.css
        maxWidth={false} // Use full screen width
        sx={{ textAlign: 'center', py: 5 }}
      >
        <CircularProgress sx={{ color: '#8b4513' }} />
        <Typography sx={{ fontFamily: '"Georgia", serif', mt: 2, color: '#5c3c10' }}>
          Unearthing forgotten chronicles...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container 
        className="town-square-container" // Using class from TownSquare.css
        maxWidth={false} // Use full screen width
        sx={{ py: 5 }}
      >
        <Alert severity="error" sx={{ fontFamily: '"Georgia", serif', backgroundColor: '#ffcccb', color: '#721c24' }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      className="town-square-container" // Using class from TownSquare.css
      maxWidth={false} // Use full screen width
      sx={{ py: 4 }}
    >
      <Box 
        className="town-square-header" 
        sx={{ 
          mb: 4, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}
      > {/* Using class from TownSquare.css */}
        <Typography 
          variant="h3" 
          component="h1"
          sx={{ 
            fontFamily: '"Georgia", serif', // Changed from Cinzel Decorative to Georgia
            color: '#8b4513', // SaddleBrown
            fontWeight: 'bold',
            fontSize: '2.5em', // Aligning with .town-square-header h1 if needed
            textShadow: '1px 1px 1px #deb887' // BurlyWood
          }}
        >
          The Benny Archives
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/benny-journal/new')} // Corrected navigation path
          sx={{
            fontFamily: '"Georgia", serif',
            backgroundColor: '#8B4513', // Primary button color
            color: '#F5F5F5',
            '&:hover': {
              backgroundColor: '#A0522D'
            },
            textTransform: 'none',
            fontSize: '1rem',
            padding: '8px 16px',
          }}
        >
          Create Journal Entry
        </Button>
      </Box>

      {journalEntries.length === 0 && !loading && (
         <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" sx={{ color: '#5c3c10', fontStyle: 'italic', fontFamily: '"Georgia", serif' }}>
              The archives appear barren. No new tales have been scribed.
            </Typography>
         </Box>
      )}

      {/* List layout for consistent, user-friendly display */}
      <Stack spacing={2} sx={{ 
        maxWidth: { xs: '100%', sm: '800px', md: '1000px', lg: '1200px', xl: '1400px' }, 
        margin: '0 auto',
        px: { xs: 1, sm: 2 }
      }}>
        {journalEntries.map((entry, index) => (
          <Card
            key={entry.id}
            className="post-it-note"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              border: '1px solid #d2b48c',
              backgroundColor: '#fffacd',
              width: '100%',
              minHeight: '120px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              pr: 2,
              minWidth: 0 // Allows flex item to shrink below content width
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  className="post-title"
                  sx={{ 
                    fontFamily: '"Georgia", serif', 
                    color: '#5c3c10', 
                    fontSize: { xs: '1.1rem', md: '1.2rem' },
                    fontWeight: 'bold',
                    borderBottom: 'none' // Remove border for cleaner list look
                  }}
                >
                  {entry.title}
                </Typography>
                {entry.audioURL && (
                  <Chip
                    icon={<MicIcon />}
                    label="Benny Audio Included ™"
                    size="small"
                    sx={{
                      backgroundColor: '#8B4513',
                      color: '#F5F5F5',
                      fontFamily: '"Georgia", serif',
                      fontSize: '0.7rem',
                      pointerEvents: 'none',
                      '& .MuiChip-icon': {
                        color: '#F5F5F5',
                      }
                    }}
                  />
                )}
              </Box>
              <Typography 
                variant="caption" 
                display="block" 
                className="post-meta"
                sx={{ 
                  mb: { xs: 0.5, md: 1 }, 
                  fontStyle: 'italic', 
                  color: '#7a5c3f', 
                  fontFamily: '"Georgia", serif',
                  fontSize: '0.75rem'
                }}
              >
                by {entry.userName} • {entry.createdAt?.toDate ? new Date(entry.createdAt.toDate()).toLocaleDateString() : 'Unknown date'}
              </Typography>
              {entry.description && (
                <Typography 
                  component="div"
                  variant="body2"
                  className="post-content-summary"
                  sx={{ 
                    color: '#5c3c10', 
                    fontFamily: '"Georgia", serif',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 2, lg: 3 }, // Show more lines on larger screens
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mb: 2,
                    fontSize: '0.9rem',
                    maxWidth: '100%' // Prevent text from overflowing
                  }}
                >
                  {linkifyWikiEntries(entry.description, wikiEntries)}
                </Typography>
              )}
            </CardContent>
            
            {/* Action buttons on the right side */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'column' }, 
              justifyContent: 'center', 
              alignItems: { xs: 'center', sm: 'flex-end' },
              p: { xs: 1, sm: 2 }, 
              gap: 1,
              minWidth: { xs: 'auto', sm: '180px' },
              flexShrink: 0
            }}>
              {/* Edit button - show if user is admin or entry author */}
              {currentUser && (firestoreUser?.roles?.admin || entry.userId === currentUser.uid) && (
                <IconButton
                  onClick={() => navigate(`/benny-journal/${entry.id}/edit`)}
                  sx={{
                    backgroundColor: '#556B2F',
                    color: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#6B8E23'
                    },
                    width: 48,
                    height: 48,
                  }}
                  title="Edit journal entry"
                >
                  <EditIcon />
                </IconButton>
              )}
              {entry.downloadURL && (
                <Button 
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/view-pdf/${entry.id}`, { 
                    state: { 
                      pdfUrl: entry.downloadURL,
                      journalTitle: entry.title,
                      audioURL: entry.audioURL,
                      audioFileName: entry.audioFileName,
                      from: '/benny-journal'
                    } 
                  })}
                  sx={{ 
                    fontFamily: '"Georgia", serif', 
                    backgroundColor: '#8B4513',
                    color: '#F5F5F5',
                    '&:hover': { 
                      backgroundColor: '#A0522D'
                    },
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    padding: '6px 12px',
                    minWidth: '140px'
                  }}
                >
                  View Journal
                </Button>
              )}
              {entry.microsoftLink && (
                <Button 
                  variant="contained" 
                  href={entry.microsoftLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    fontFamily: '"Georgia", serif',
                    backgroundColor: '#556B2F',
                    color: '#F5F5F5',
                    '&:hover': { backgroundColor: '#6B8E23' },
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    padding: '6px 12px',
                    minWidth: '140px'
                  }}
                >
                  Open in Docs
                </Button>
              )}
              {!entry.downloadURL && !entry.microsoftLink && (
                <Typography 
                  variant="caption" 
                  sx={{
                    fontFamily: '"Georgia", serif', 
                    color: '#7a5c3f', 
                    fontStyle: 'italic',
                    fontSize: '0.75rem'
                  }}
                >
                  No attachments
                </Typography>
              )}
            </Box>
          </Card>
        ))}
      </Stack>
    </Container>
  );
};

export default BennyJournalDisplay;
